import React from "react";

const GlobalDateRangeWithTime = ({
  className = "",
  onChange,
  startDate,
  endDate,
  startTime,
  endTime,
  startDateName = "startDate",
  endDateName = "endDate",
  startTimeName = "startTime",
  endTimeName = "endTime",
  min = new Date().toISOString().split("T")[0],
  minTime = "00:00",
  maxTime = "23:59",
  invalid = false,
  disabled = false,
  outputFormat = "string",

  // New flexible props
  layout = "full", // "full" | "dateOnly" | "timeOnly" | "dateWithTime" | "custom"
  showStartDate = true,
  showEndDate = false,
  showStartTime = false,
  showEndTime = false,

  // Label customization
  showLabels = true,
  labelStartText = "Start:",
  labelEndText = "End:",
  labelStartDateText = "Date:",
  labelStartTimeText = "Time:",
  labelEndDateText = "Date:",
  labelEndTimeText = "Time:",

  // Container styles
  containerClassName = "flex flex-col gap-4",
  rowClassName = "flex gap-2 items-center flex-wrap",
  labelClassName = "font-semibold min-w-fit",
  inputClassName = "px-3 py-2 border rounded",
}) => {
  const dateToString = (date) => {
    if (!date) return "";
    if (typeof date === "string") return date;
    if (date instanceof Date) {
      return date.toISOString().split("T")[0];
    }
    return "";
  };

  const stringToDate = (dateString) => {
    if (!dateString) return null;
    if (dateString instanceof Date) return dateString;
    if (typeof dateString === "string") {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? null : date;
    }
    return null;
  };

  const formatOutput = (value, fieldType) => {
    if (fieldType === "time") return value;

    switch (outputFormat) {
      case "date":
        return stringToDate(value);
      case "string":
        return typeof value === "string" ? value : dateToString(value);
      case "auto":
        if (typeof startDate === "string" || typeof endDate === "string") {
          return typeof value === "string" ? value : dateToString(value);
        } else {
          return stringToDate(value);
        }
      default:
        return value;
    }
  };

  const displayStartDate = dateToString(startDate);
  const displayEndDate = dateToString(endDate);
  const displayStartTime =
    typeof startTime === "string" ? startTime : startTime?.toString() || "";
  const displayEndTime =
    typeof endTime === "string" ? endTime : endTime?.toString() || "";

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    const isDateField = name === startDateName || name === endDateName;
    if (isDateField) {
      formattedValue = formatOutput(value, "date");
    }

    const event = {
      target: {
        name: name,
        value: formattedValue,
      },
    };

    if (onChange) {
      onChange(event, name);
    }
  };

  // Determine what to show based on layout or explicit props
  const shouldShowStartDate =
    layout === "full" ||
    layout === "dateOnly" ||
    layout === "dateWithTime" ||
    showStartDate;
  const shouldShowStartTime =
    layout === "full" ||
    layout === "timeOnly" ||
    layout === "dateWithTime" ||
    showStartTime;
  const shouldShowEndDate = layout === "full" || showEndDate;
  const shouldShowEndTime = layout === "full" || showEndTime;

  const inputBaseClass = `flex-1 px-3 py-2 border rounded ${inputClassName} ${
    invalid ? "border-red-500" : "border-gray-300"
  } ${disabled ? "bg-gray-100 cursor-not-allowed" : ""}`;

  return (
    <div className={`${containerClassName} ${className}`}>
      {/* Start Date/Time Row */}
      {(shouldShowStartDate || shouldShowStartTime) && (
        <div className={rowClassName}>
          {showLabels && (shouldShowStartDate || shouldShowStartTime) && (
            <label className={labelClassName}>{labelStartText}</label>
          )}

          {/* Start Date Input */}
          {shouldShowStartDate && (
            <input
              type="date"
              value={displayStartDate}
              onChange={handleChange}
              name={startDateName}
              disabled={disabled}
              min={min}
              className={inputBaseClass}
            />
          )}

          {/* Start Time Input */}
          {shouldShowStartTime && (
            <input
              type="time"
              value={displayStartTime}
              onChange={handleChange}
              name={startTimeName}
              disabled={disabled}
              min={minTime}
              max={maxTime}
              className={inputBaseClass}
            />
          )}
        </div>
      )}

      {/* End Date/Time Row */}
      {(shouldShowEndDate || shouldShowEndTime) && (
        <div className={rowClassName}>
          {showLabels && (shouldShowEndDate || shouldShowEndTime) && (
            <label className={labelClassName}>{labelEndText}</label>
          )}

          {/* End Date Input */}
          {shouldShowEndDate && (
            <input
              type="date"
              value={displayEndDate}
              onChange={handleChange}
              name={endDateName}
              disabled={disabled}
              min={min}
              className={inputBaseClass}
            />
          )}

          {/* End Time Input */}
          {shouldShowEndTime && (
            <input
              type="time"
              value={displayEndTime}
              onChange={handleChange}
              name={endTimeName}
              disabled={disabled}
              min={minTime}
              max={maxTime}
              className={inputBaseClass}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalDateRangeWithTime;
